import {
  CalendarEvent,
  IDPrefixes,
  Task,
  TaskType,
  Timestamp,
  Todo,
  WeekDays,
} from '@griply/types';
import {
  addMilliseconds,
  endOfDay,
  format,
  isSameDay,
  startOfDay,
} from 'date-fns';

import { HALF_HOUR } from './constants';
import { createCompositeId } from './encoded-ids/composite-id';
import { getCalendarEventReferenceDate } from './get-calendar-event-reference-date';
import { getCalendarOccurrenceCheckState } from './get-calendar-occurrence-check-state';
import {
  getCalendarTaskEndDate,
  getCalendarTaskStartDate,
} from './get-calendar-task-dates';
import { getTaskPlanning } from './get-task-planning';
import { isNumber } from './is-number';
import { isTimeslotAction } from './is-timeslot-action';
import { getTimeframeSchedules } from './schedules';

export type GetTasksCalendarEventsOptions = {
  startDate: Timestamp;
  endDate: Timestamp;
  today: Timestamp;
  weekStartsOn: WeekDays;
  hideCompleted?: boolean;
};

const getTaskMeta = (
  task: Task,
): Pick<
  CalendarEvent,
  'taskId' | 'actionType' | 'actionMetaId' | 'goalId' | 'lifeAreaId'
> => ({
  taskId: task.id,
  actionType: task.action?.type,
  actionMetaId: task.action?.meta?.id ?? undefined,
  goalId: task.goalId ?? undefined,
  lifeAreaId: task.lifeAreaId ?? undefined,
});

const withCheckState = (
  events: CalendarEvent[],
  task: Task,
  { today, weekStartsOn }: GetTasksCalendarEventsOptions,
): CalendarEvent[] =>
  events.map((event) => ({
    ...event,
    ...getCalendarOccurrenceCheckState(
      task,
      getCalendarEventReferenceDate(event.start, today),
      { today, weekStartsOn },
    ),
  }));

const getRegularTaskEvents = (
  task: Task,
  options: GetTasksCalendarEventsOptions,
): CalendarEvent[] => {
  const { hideCompleted } = options;
  if (hideCompleted && task.completedAt) {
    return [];
  }

  const events: CalendarEvent[] = [];
  const meta = getTaskMeta(task);
  const start = getCalendarTaskStartDate(task);
  const end = getCalendarTaskEndDate(task);
  const deadline = task.endStrategy?.deadline;

  // Goal/life-area timeblock tasks render as their own card (no flag slot), so a
  // coinciding hard deadline keeps its standalone marker rather than merging.
  const isActionTimeslot = isTimeslotAction(task.action?.type);

  // When the hard deadline (deadlineDeadline) lands on the same day as the
  // scheduled occurrence, collapse the duplicate all-day marker into the
  // scheduled event as a flag instead of rendering the task twice.
  const deadlineOnScheduledDay =
    !!task.deadlineDeadline &&
    !!deadline &&
    !isActionTimeslot &&
    isSameDay(task.deadlineDeadline, deadline);

  // allDay
  if (!start && deadline) {
    const dateStr = format(deadline, 'yyyy-MM-dd');
    events.push({
      id: task.id,
      title: task.name,
      start: { date: dateStr },
      end: { date: dateStr },
      allDay: true,
      date: deadline,
      duration: (task as Todo).timeslot?.duration || HALF_HOUR,
      showDeadlineFlag: deadlineOnScheduledDay || undefined,
      ...meta,
    });
  }

  // timeslot
  if (start && end) {
    events.push({
      id: task.id,
      title: task.name,
      start: { dateTime: start.toISOString() },
      end: { dateTime: end.toISOString() },
      allDay: false,
      date: deadline ?? undefined,
      duration: (task as Todo).timeslot?.duration || HALF_HOUR,
      showDeadlineFlag: deadlineOnScheduledDay || undefined,
      ...meta,
    });
  }

  // Deadline marker (separate all-day event) — skipped when it coincides with
  // the scheduled day, where it surfaces as a flag on the event above instead.
  if (task.deadlineDeadline && !deadlineOnScheduledDay) {
    const deadlineId = createCompositeId(IDPrefixes.CalendarEvent, [
      task.id,
      String(task.deadlineDeadline.getTime()),
    ]);
    events.push({
      id: deadlineId,
      title: task.name,
      start: { date: format(task.deadlineDeadline, 'yyyy-MM-dd') },
      end: { date: format(task.deadlineDeadline, 'yyyy-MM-dd') },
      allDay: true,
      date: task.deadlineDeadline,
      isDeadline: true,
      ...meta,
    });
  }

  return withCheckState(events, task, options);
};

const getHabitTaskEvents = (
  task: Task,
  options: GetTasksCalendarEventsOptions,
): CalendarEvent[] => {
  const { startDate, endDate, today, weekStartsOn, hideCompleted } = options;
  if (!task.schedules?.length || task.hideOnTodo) {
    return [];
  }

  // A completed habit stops recurring at its completedAt day, toggle-independent.
  const cappedEndDate = task.completedAt
    ? new Date(
        Math.min(endOfDay(task.completedAt).getTime(), endDate.getTime()),
      )
    : endDate;

  const timeframeSchedules = getTimeframeSchedules(task.schedules, {
    startDate,
    endDate: cappedEndDate,
  });
  if (!timeframeSchedules.length) {
    return [];
  }

  const plannedOccurrences = getTaskPlanning(task, {
    weekStartsOn,
    startDate: startOfDay(startDate),
    endDate: endOfDay(cappedEndDate),
    today,
    uncompletedOnly: hideCompleted,
  });
  if (!plannedOccurrences.length) {
    return [];
  }

  const events: CalendarEvent[] = [];
  const meta = getTaskMeta(task);

  for (const occurrence of plannedOccurrences) {
    const duration = occurrence.timeslot?.duration ?? HALF_HOUR;
    const hasTime = isNumber(occurrence.timeslot?.startTime);

    const occStart = hasTime
      ? addMilliseconds(
          startOfDay(occurrence.date),
          occurrence.timeslot!.startTime!,
        )
      : undefined;
    const occEnd = occStart ? addMilliseconds(occStart, duration) : undefined;

    const eventDate = occStart ?? occurrence.date;
    const eventId = createCompositeId(IDPrefixes.CalendarEvent, [
      task.id,
      String(eventDate.getTime()),
      String(duration),
    ]);

    // timeslot
    if (hasTime && occStart && occEnd) {
      events.push({
        id: eventId,
        title: task.name,
        start: { dateTime: occStart.toISOString() },
        end: { dateTime: occEnd.toISOString() },
        allDay: false,
        date: occurrence.date,
        occurrenceId: occurrence.id,
        duration,
        ...meta,
      });
      continue;
    }

    // allDay
    const dateStr = format(occurrence.date, 'yyyy-MM-dd');
    events.push({
      id: eventId,
      title: task.name,
      start: { date: dateStr },
      end: { date: dateStr },
      allDay: true,
      date: occurrence.date,
      occurrenceId: occurrence.id,
      duration,
      ...meta,
    });
  }

  return withCheckState(events, task, options);
};

export const getTasksCalendarEvents = (
  tasks: Task[],
  options: GetTasksCalendarEventsOptions,
): CalendarEvent[] => {
  const events: CalendarEvent[] = [];

  for (const task of tasks) {
    if ([TaskType.Habit, TaskType.Repeating].includes(task.type)) {
      events.push(...getHabitTaskEvents(task, options));
      continue;
    }

    events.push(...getRegularTaskEvents(task, options));
  }

  return events;
};
